import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardCopy } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  EnvironmentView,
  ReleaseDetail,
  ReleaseJourney,
} from "../../shared/types/api";
import { Button, InlineAlert } from "../../shared/ui";
import { isFinished } from "./releaseVocabulary";

/**
 * Offers the previous environment's plan, from inside the leg that needs it.
 *
 * <p>A successful deployment hands the next leg its plan automatically, which covers the ordinary
 * path. It does not cover the rest: a plan written after the deployment, a leg reached by a
 * deployment recorded before this behaviour existed, or somebody who simply wants the wording now
 * rather than at deploy time.
 *
 * <p>Offered here rather than only on the journey screen because here is where somebody notices.
 * Standing in an empty Production runbook and being told the fix is one screen back is the kind of
 * thing people work around by retyping — which is what the copy exists to prevent.
 */
export function CopyPlanFromPrevious({
  release,
  leg,
  onCopied,
}: {
  release: ReleaseDetail;
  leg: EnvironmentView;
  onCopied: () => void;
}) {
  const releaseId = release.id;
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  const journey = useQuery({
    queryKey: ["release-journey", releaseId],
    queryFn: () => api.get<ReleaseJourney>(`/api/v1/releases/${releaseId}/journey`),
  });

  const legs = journey.data?.legs ?? [];
  const here = legs.findIndex((row) => row.environment.id === leg.id);
  const previous = here > 0 ? legs[here - 1] : null;
  const hasSomethingToCopy =
    previous != null && previous.scopedPrerequisites + previous.scopedSteps > 0;

  /* Not offered on a finished release. The server refuses — its runbook is part of the record —
     and a button that returns a conflict reads as a fault rather than as the rule it is. */
  if (
    !can("RELEASE_EDIT")
    || isFinished(release.status)
    || !hasSomethingToCopy
    || previous == null
  ) {
    return null;
  }

  const copy = async () => {
    setCopying(true);
    setError(null);
    try {
      await api.post(`/api/v1/releases/${releaseId}/journey/copy-plan`, {
        fromEnvironmentId: previous.environment.id,
        toEnvironmentId: leg.id,
      });
      await queryClient.invalidateQueries({ queryKey: ["release-journey", releaseId] });
      await queryClient.invalidateQueries({ queryKey: ["release-prerequisites", releaseId] });
      await queryClient.invalidateQueries({ queryKey: ["release-execution", releaseId] });
      onCopied();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not copy that plan.");
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="space-y-2">
      {error && <InlineAlert tone="error">{error}</InlineAlert>}
      <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg border border-dashed border-ink-200 px-4 py-3">
        <p className="text-sm text-ink-600">
          {previous.environment.name} has a plan. Copying brings the wording across and nothing
          else — no ticks, no evidence, no times.
        </p>
        <Button
          variant="secondary"
          size="sm"
          icon={<ClipboardCopy className="h-4 w-4" />}
          loading={copying}
          onClick={() => void copy()}
        >
          Copy from {previous.environment.name}
        </Button>
      </div>
    </div>
  );
}
